import {getRankedFullDivisionText} from '../../../../shared/leaderboard_data/LeaderboardEnums'
import {LeaderboardRowData} from '../../../../shared/leaderboard_data/LeaderboardInterfaces'
import {ColonistMembershipProduct} from '../../../../shared/store/ColonistMembershipProduct'
import {UIImagePathController} from '../../../shared/scripts/UIEnums.ts'
import {ViewPopupRankedGameDescription} from '../ui_popup/ViewPopupRankedGameDescription'
import {navigateToPage} from '../../../shared/scripts/ui_utils/ClientUtils'
import {createTDIn, setElementInnerHTML} from '../../../shared/scripts/ui_utils/DomUtils'
import {getProfileURI} from '../../../shared/scripts/ui_utils/TestableClientUtils'
import {clientIntl} from '../../../shared/scripts/ClientI18n'
import {TranslationObject} from '@colonist/i18n'
import {ColonistMembershipType} from '@colonist/types'
import {ContinentCodes, CountryCodes, RankedDivisionTypes, RankedSubDivisionTypes} from '@colonist/leaderboard-types'
import {getLobbyImagePath} from '../../../shared/scripts/ui_utils/Utils'
import {getCountryOrContinentImageLink} from '../../../shared/scripts/ui_utils/CountryFlagUtils.ts'
import {createTooltipIn} from '../../../shared/scripts/ui_utils/TooltipUtils.ts'

export abstract class UIWLeaderboardBaseTableRowView {
    readonly rowData: LeaderboardRowData

    readonly tableRow: HTMLTableRowElement
    readonly tdRank: HTMLTableCellElement
    readonly tdDivision: HTMLTableCellElement
    readonly tdUsername: HTMLTableCellElement

    private readonly onLeaderboardUrlChange: (leaderboardUrl: string) => void

    constructor(container: HTMLElement, rowData: LeaderboardRowData, index: number, onLeaderboardUrlChange: (leaderboardUrl: string) => void) {
        this.rowData = rowData
        this.tableRow = (container as HTMLTableSectionElement).insertRow(index)
        const tableRowID = rowData.myData ? 'my_leaderboard_cell' : 'leaderboard_cell'

        this.tdRank = createTDIn(this.tableRow, tableRowID)
        this.tdDivision = createTDIn(this.tableRow, tableRowID)
        this.tdUsername = createTDIn(this.tableRow, `${tableRowID} truncate-text`)
        if(rowData.myData && rowData.division == RankedDivisionTypes.None) UIWLeaderboardBaseTableRowView.hideCell(this.tdDivision)

        this.tableRow.addEventListener('click', () => {
            const profileRankedSectionUrl = getProfileURI(rowData.username) + '#ranked'
            navigateToPage(profileRankedSectionUrl, true)
        })
        this.onLeaderboardUrlChange = onLeaderboardUrlChange
    }

    protected update(rowData: LeaderboardRowData) {
        const includeNumberHash = typeof rowData.rank == 'number' ? '#' : ''
        const rankText = `${includeNumberHash}${rowData.rank}`
        setElementInnerHTML(this.tdRank, {key: 'strings:colonistCommon.utils.#buffer', options: {value: rankText}})
        const membership = rowData.membershipType
        if(membership !== undefined) {
            const membershipImage = this.getMembershipImg(membership)
            const membershipProduct = ColonistMembershipProduct.getMembershipProduct(membership)
            createTooltipIn(membershipImage, membershipProduct.membershipTitle, 'right')
            this.tdRank.appendChild(membershipImage)
        }

        const divisionImg = UIWLeaderboardBaseTableRowView.getDivisionImage(rowData.division)
        createTooltipIn(divisionImg, getRankedFullDivisionText(rowData.division, rowData.subdivision), 'right')
        this.tdDivision.replaceChildren(divisionImg)
        const subdivisionImg = UIWLeaderboardBaseTableRowView.getSubdivisionImage(rowData.subdivision)
        if(subdivisionImg != undefined) this.tdDivision.appendChild(subdivisionImg)

        const countryFlagImg = this.getCountryOrContinentImage(rowData.continentCode, rowData.countryCode, rowData.countryLeaderboardUrl)

        const countryOrContinentName = UIWLeaderboardBaseTableRowView.getCountryOrContinentName(rowData)

        createTooltipIn(countryFlagImg, countryOrContinentName, 'right')
        this.tdUsername.replaceChildren(rowData.username, countryFlagImg)
    }

    // eslint-disable-next-line class-methods-use-this
    protected getMembershipImg(membershipType: ColonistMembershipType): HTMLImageElement {
        // Can't use `createImgIn` here since that adds an image, and we need to replace the existing image
        const image: HTMLImageElement = document.createElement('img')
        image.classList.add('membership_icon')
        const membershipProduct = ColonistMembershipProduct.getMembershipProduct(membershipType)
        image.src = getLobbyImagePath(membershipProduct.lobbyImage)
        image.onclick = () => {
            navigateToPage('/store', true)
        }
        return image
    }

    protected static getCountryOrContinentName(rowData: LeaderboardRowData): TranslationObject {
        if(rowData.countryCode) return {key: 'strings:colonistCommon.utils.#buffer', options: {value: clientIntl.getCountryName(rowData.countryCode)}}
        if(rowData.continentCode) return {key: `strings:leaderboardPage.tabs.continents.${rowData.continentCode}`}
        return {key: 'strings:leaderboardPage.earth'}
    }

    private static getDivisionImage(division: RankedDivisionTypes): HTMLImageElement {
        // Can't use `createImgIn` here since that adds an image, and we need to replace the existing image
        const lobbyImage = UIImagePathController.getRankedDivisionImage(division)
        const image: HTMLImageElement = document.createElement('img')
        if(division != RankedDivisionTypes.None) image.src = getLobbyImagePath(lobbyImage)
        image.style.cursor = 'pointer'
        image.onclick = (event: MouseEvent) => {
            event.stopPropagation()
            new ViewPopupRankedGameDescription()
        }
        return image
    }

    private static getSubdivisionImage(subdivision: RankedSubDivisionTypes): HTMLImageElement | undefined {
        // Can't use `createImgIn` here since that adds an image, and we need to replace the existing image
        const lobbyImage = UIImagePathController.getRankedSubdivisionImage(subdivision)
        if(lobbyImage == undefined) return

        const image: HTMLImageElement = document.createElement('img')
        image.src = getLobbyImagePath(lobbyImage)
        image.style.cursor = 'pointer'
        image.onclick = (event: MouseEvent) => {
            event.stopPropagation()
            new ViewPopupRankedGameDescription()
        }
        return image
    }

    protected getCountryOrContinentImage(continentCode: ContinentCodes | undefined, countryCode: CountryCodes | undefined, countryLeaderboardUrl: string | undefined): HTMLImageElement {
        // May 30, 2022. Logan: Can't use `createImgIn` here since that adds an image, and we need to replace the existing image
        const image: HTMLImageElement = document.createElement('img')
        image.src = getCountryOrContinentImageLink(countryCode, continentCode)
        image.classList.add('flag')

        if(countryLeaderboardUrl != undefined) {
            image.addEventListener('click', (mouseEvent: MouseEvent) => {
                mouseEvent.stopPropagation()
                this.onLeaderboardUrlChange(countryLeaderboardUrl)
            })
        }
        return image
    }

    private static hideCell(cell: HTMLTableCellElement) {
        cell.classList.add('hidden')
    }
}
